import React, { ReactNode } from 'react'
import { RadioButton, Typography } from '@telavita-core/react-design-kit'

import { Option } from '../../types/option'

import { ReactComponent as ComplexityLowSvg } from 'static/svg/complexity-low-icon.svg'
import { ReactComponent as ComplexityModerateSvg } from 'static/svg/complexity-moderate-icon.svg'
import { ReactComponent as ComplexityNormalSvg } from 'static/svg/complexity-normal-icon.svg'
import { ReactComponent as ComplexitySeriousSvg } from 'static/svg/complexity-serious-icon.svg'
import { ReactComponent as CheckSvg } from 'static/svg/check-white.svg'

import * as S from './styles'
import { Controller, useFormContext } from 'react-hook-form'
import { MedicalRecordFormData } from '../../medicalRecordSchema'
import { RadioButtonErrorWrapper } from '../RadioButtonErrorWrapper'
import { useMedicalRecordForm } from '../../hooks/useMedicalRecordForm'

interface OptionWithIcon extends Option {
  icon: ReactNode
}

const radioOptions: OptionWithIcon[] = [
  {
    id: 'patient-case-complexity-normal',
    name: 'Nenhum',
    icon: <ComplexityNormalSvg />,
  },
  {
    id: 'patient-case-complexity-low',
    name: 'Baixo',
    icon: <ComplexityLowSvg />,
  },
  {
    id: 'patient-case-complexity-moderate',
    name: 'Moderado',
    icon: <ComplexityModerateSvg />,
  },
  {
    id: 'patient-case-complexity-serious',
    name: 'Grave',
    icon: <ComplexitySeriousSvg />,
  },
]

export function PatientCaseComplexity(): JSX.Element {
  const { form } = useMedicalRecordForm()
  const {
    control,
    formState: { errors },
  } = useFormContext<MedicalRecordFormData>()

  const errorMessage = errors.patient_case_complexity?.message
  const hasErrorMessage = Boolean(errorMessage)

  const isSelected = (
    currentOptionId: string | number,
    currrentOptionSelectedId: string | number
  ) => currentOptionId === currrentOptionSelectedId

  const lastComplexity = form?.last_patient_case_complexity
  const lastComplexityId = `patient-case-complexity-${lastComplexity?.toLowerCase()}`
  const lastComplexityOption = radioOptions.find(option => option.id === lastComplexityId)

  return (
    <S.PatientCaseComplexityContainer>
      <Typography weight='bolder' variant='content2'>
        De acordo com a sua avaliação, qual é a complexidade atual do caso? *
      </Typography>
      {lastComplexity && (
        <S.BoxInfo type={lastComplexity}>
          <Typography variant='content3'>
          Na última consulta, foi avaliado como {' '}
            <strong>{lastComplexityOption?.name}</strong>.
          </Typography>
        </S.BoxInfo>
      )}
      <RadioButtonErrorWrapper errorMessage={errorMessage}>
        <S.RadioButtonOptions>
          {radioOptions.map((option) => {
            return (
              <Controller
                key={option.id}
                control={control}
                name='patient_case_complexity'
                render={({ field }) => (
                  <S.RadioButtonOption
                    key={option.id}
                    isSelected={isSelected(option.id, field.value)}
                    hasError={hasErrorMessage}
                  >
                    <RadioButton
                      {...field}
                      onSelect={() => field.onChange(option.id)}
                      isSelected={isSelected(option.id, field.value)}
                    >
                      {option.icon}
                      <Typography variant='content3' center>
                        {option.name}
                      </Typography>
                    </RadioButton>
                    {isSelected(option.id, field.value) && (
                      <span>
                        <CheckSvg width={16} height={16} />
                      </span>
                    )}
                  </S.RadioButtonOption>
                )}
              />
            )
          })}
        </S.RadioButtonOptions>
      </RadioButtonErrorWrapper>
    </S.PatientCaseComplexityContainer>
  )
}
